import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';

// YYYY-MM-DD (local)
const toYMD = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const formatTime = (t) => (t ? String(t).substring(0, 5) : '');
const DAY_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
const MONTH_TH = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

// การ์ดการสอนแทนรายวัน 1 รายการ สำหรับมุมมองมือถือ
const DayCard = ({ r }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">คาบ {r.period_order} · ม.{r.tb_class_c_id}/{r.r_no}</div>
      <span className="fs-xs text-muted">{formatTime(r.period_begin)} - {formatTime(r.period_end)}</span>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">วิชา:</span> {r.sub_subid} {r.sub_subname}</div>
      <div>
        <span className="text-muted">ครูเจ้าของวิชา (ลา):</span> {r.absent_teacher_name}
        {r.absent_teacher_phone && <span className="text-muted"> ☎ {r.absent_teacher_phone}</span>}
      </div>
      <div>
        <span className="text-muted">ครูสอนแทน:</span> <Badge bg="info">{r.sub_teacher_name}</Badge>
        {r.sub_teacher_phone && <span className="text-muted"> ☎ {r.sub_teacher_phone}</span>}
      </div>
      {r.reason && <div><span className="text-muted">เหตุผล:</span> {r.reason}</div>}
    </div>
  </div>
)

// การ์ดสรุปการสอนแทนรายเดือนของครู 1 คน สำหรับมุมมองมือถือ
const SummaryCard = ({ r, idx, detailRows, onToggle }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">#{idx + 1} {r.t_name}</div>
      <Button variant="outline-secondary" size="sm" onClick={onToggle}>
        {detailRows && detailRows !== 'loading' ? 'ซ่อน' : 'ดู'}
      </Button>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">จำนวนคาบ:</span> <strong>{r.total_periods}</strong></div>
      <div><span className="text-muted">จำนวนวัน:</span> {r.total_days}</div>
    </div>
    {detailRows && detailRows !== 'loading' && (
      <div className="mt-2 pt-2 border-top">
        {detailRows.map(d => (
          <div key={d.sub_id} className="fs-xs mb-1 pb-1 border-bottom">
            {toYMD(d.sub_date)} · คาบ {d.period_order} · {d.sub_subid} {d.sub_subname} · ม.{d.tb_class_c_id}/{d.r_no}
            <div className="text-muted">แทนครู {d.absent_teacher_name} — {d.reason || '-'}</div>
          </div>
        ))}
      </div>
    )}
  </div>
)

export default function SubstituteOverview() {
  const [skin, setSkin] = useState((localStorage.getItem('skin-mode')) ? 'dark' : '');
  const { setLoading } = useLoading();

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(toYMD(today));
  const [selectedMonth, setSelectedMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  );

  const [dayRows, setDayRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [detail, setDetail] = useState({}); // keyed by t_id → rows | 'loading'
  const [dayView, setDayView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [summaryView, setSummaryView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  // ----- daily oversight -----
  const loadDay = useCallback(() => {
    if (!selectedDate) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubstitutionsByDate/${selectedDate}`,
      { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setDayRows(res.data || []))
      .catch(err => { console.error("Error loading day overview:", err); setDayRows([]); })
      .finally(() => setLoading(false));
  }, [selectedDate, setLoading]);

  useEffect(() => { loadDay(); }, [loadDay]);

  // ----- monthly summary -----
  const loadSummary = useCallback(() => {
    if (!selectedMonth) return;
    const [y, m] = selectedMonth.split('-');
    setDetail({});
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubstitutionMonthlySummary/${y}/${Number(m)}`,
      { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setSummary(res.data || []))
      .catch(err => { console.error("Error loading monthly summary:", err); setSummary([]); });
  }, [selectedMonth]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const toggleDetail = (tId) => {
    if (detail[tId] && detail[tId] !== 'loading') {
      setDetail(prev => { const n = { ...prev }; delete n[tId]; return n; });
      return;
    }
    const [y, m] = selectedMonth.split('-');
    setDetail(prev => ({ ...prev, [tId]: 'loading' }));
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubstitutionMonthlyDetail/${y}/${Number(m)}/${tId}`,
      { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setDetail(prev => ({ ...prev, [tId]: res.data || [] })))
      .catch(err => {
        console.error("Error loading detail:", err);
        setDetail(prev => { const n = { ...prev }; delete n[tId]; return n; });
      });
  };

  const dayName = DAY_TH[new Date(selectedDate).getDay()];
  const [my, mm] = selectedMonth.split('-');
  const grandTotalPeriods = summary.reduce((s, r) => s + Number(r.total_periods || 0), 0);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการสามัญ</Link></li>
              <li className="breadcrumb-item active" aria-current="page">การสอนแทน</li>
            </ol>
            <h4 className="main-title mb-0">ภาพรวมการสอนแทน</h4>
          </div>
        </div>

        {/* ---- daily oversight ---- */}
        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">การสอนแทนรายวัน</Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${dayView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setDayView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${dayView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setDayView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <label className="form-label">เลือกวันที่</label>
                    <input type="date" className="form-control"
                      value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
                    <small className="text-muted">วัน{dayName}</small>
                  </Col>
                </Row>
                {dayRows.length === 0 ? (
                  <div className="text-center py-3">ไม่มีการสอนแทนในวันนี้</div>
                ) : dayView === 'card' ? (
                  <Row className="g-3">
                    {dayRows.map(r => (
                      <Col key={r.sub_id} xs={12} md={6} xl={4}>
                        <DayCard r={r} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr className="text-center">
                      <th style={{ width: "70px" }}>คาบ</th>
                      <th style={{ width: "110px" }}>เวลา</th>
                      <th>วิชา</th>
                      <th style={{ width: "80px" }}>ชั้น</th>
                      <th>ครูเจ้าของวิชา (ลา)</th>
                      <th>ครูสอนแทน</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map(r => (
                      <tr key={r.sub_id} className="text-center align-middle">
                        <th scope="row">{r.period_order}</th>
                        <td>{formatTime(r.period_begin)} - {formatTime(r.period_end)}</td>
                        <td className="text-start">{r.sub_subid} {r.sub_subname}</td>
                        <td>{r.tb_class_c_id}/{r.r_no}</td>
                        <td>
                          {r.absent_teacher_name}
                          {r.absent_teacher_phone && (
                            <div className="fs-xs text-muted">☎ {r.absent_teacher_phone}</div>
                          )}
                        </td>
                        <td>
                          <Badge bg="info">{r.sub_teacher_name}</Badge>
                          {r.sub_teacher_phone && (
                            <div className="fs-xs text-muted">☎ {r.sub_teacher_phone}</div>
                          )}
                        </td>
                        <td className="text-start">{r.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ---- monthly compensation summary ---- */}
        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">สรุปการสอนแทนรายเดือน (สำหรับค่าตอบแทน)</Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${summaryView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setSummaryView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${summaryView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setSummaryView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <label className="form-label">เลือกเดือน</label>
                    <input type="month" className="form-control"
                      value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
                    <small className="text-muted">{MONTH_TH[Number(mm)]} {my}</small>
                  </Col>
                  <Col md={8} className="d-flex align-items-end justify-content-end">
                    <span className="fs-sm">
                      รวมทั้งเดือน: <strong>{grandTotalPeriods}</strong> คาบ / ครู {summary.length} คน
                    </span>
                  </Col>
                </Row>
                {summary.length === 0 ? (
                  <div className="text-center py-3">ไม่มีข้อมูลการสอนแทนในเดือนนี้</div>
                ) : summaryView === 'card' ? (
                  <Row className="g-3">
                    {summary.map((r, idx) => (
                      <Col key={r.t_id} xs={12} md={6} xl={4}>
                        <SummaryCard r={r} idx={idx} detailRows={detail[r.t_id]} onToggle={() => toggleDetail(r.t_id)} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr className="text-center">
                      <th style={{ width: "60px" }}>#</th>
                      <th className="text-start">ครูสอนแทน</th>
                      <th style={{ width: "130px" }}>จำนวนคาบ</th>
                      <th style={{ width: "130px" }}>จำนวนวัน</th>
                      <th style={{ width: "110px" }}>รายละเอียด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.length === 0 ? (
                      <tr><td colSpan={5} className="text-center">ไม่มีข้อมูลการสอนแทนในเดือนนี้</td></tr>
                    ) : (
                      summary.map((r, idx) => (
                        <React.Fragment key={r.t_id}>
                          <tr className="text-center align-middle">
                            <td>{idx + 1}</td>
                            <td className="text-start">{r.t_name}</td>
                            <td><strong>{r.total_periods}</strong></td>
                            <td>{r.total_days}</td>
                            <td>
                              <Button variant="outline-secondary" size="sm" onClick={() => toggleDetail(r.t_id)}>
                                {detail[r.t_id] && detail[r.t_id] !== 'loading' ? 'ซ่อน' : 'ดู'}
                              </Button>
                            </td>
                          </tr>
                          {detail[r.t_id] && detail[r.t_id] !== 'loading' && (
                            <tr>
                              <td colSpan={5} className="p-0">
                                <Table size="sm" className="mb-0 bg-light">
                                  <thead>
                                    <tr className="text-center">
                                      <th style={{ width: "120px" }}>วันที่</th>
                                      <th style={{ width: "60px" }}>คาบ</th>
                                      <th>วิชา</th>
                                      <th style={{ width: "80px" }}>ชั้น</th>
                                      <th>แทนครู</th>
                                      <th>เหตุผล</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail[r.t_id].map(d => (
                                      <tr key={d.sub_id} className="text-center">
                                        <td>{toYMD(d.sub_date)}</td>
                                        <td>{d.period_order}</td>
                                        <td>{d.sub_subid} {d.sub_subname}</td>
                                        <td>{d.tb_class_c_id}/{d.r_no}</td>
                                        <td>{d.absent_teacher_name}</td>
                                        <td>{d.reason || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
